import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  PhoneIncoming, PhoneOutgoing, PhoneForwarded, Clock, ArrowRight,
  X, Play, Pause, Mic, Volume2, Target, AlertTriangle, CheckCircle2,
  Zap, TrendingUp, MessageSquare, Eye, ChevronDown, ChevronUp,
  Filter, Crosshair, Shield, ArrowUpRight, BarChart3,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_LABELS = {
  buyer_inquiry: { label: 'Buyer', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  seller_inquiry: { label: 'Seller', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  listing_question: { label: 'Listing Q', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  showing_request: { label: 'Showing', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  appointment_request: { label: 'Appointment', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  callback_request: { label: 'Callback', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  direct_agent_request: { label: 'Agent Req', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  property_search: { label: 'Property', color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
  general_question: { label: 'General', color: 'bg-secondary text-muted-foreground border-border' },
  spam_or_unknown: { label: 'Spam', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  not_interested: { label: 'Not Interested', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  bad_timing: { label: 'Bad Timing', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
  out_of_market_inquiry: { label: 'Out of Market', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
};

const OUTCOME_BADGES_MAP = {
  answered: { label: 'Answered', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  voicemail: { label: 'Voicemail', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  transferred: { label: 'Transferred', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  no_answer: { label: 'No Answer', color: 'bg-secondary text-muted-foreground border-border' },
  completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  failed: { label: 'Failed', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
};

const STATUS_STYLES = {
  completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  initiated: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  failed: 'bg-red-500/10 text-red-400 border-red-500/20',
  missed: 'bg-secondary text-muted-foreground border-border',
};

const SCORECARD_OUTCOME_COLORS = {
  Booked: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  Attempted: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  Missed: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  'No Intent': 'bg-secondary text-muted-foreground border-border',
};

const INTENT_TYPE_COLORS = {
  Buyer: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  Seller: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  Listing: 'bg-purple-500/15 text-purple-400 border-purple-500/25',
  General: 'bg-secondary text-muted-foreground border-border',
};

const PERF_FILTERS = [
  { key: '', label: 'All Calls' },
  { key: 'high_score', label: 'High Score (80+)' },
  { key: 'missed', label: 'Missed Opportunities' },
  { key: 'objection', label: 'Objection Calls' },
  { key: 'buyer', label: 'Buyer' },
  { key: 'seller', label: 'Seller' },
  { key: 'listing', label: 'Listing' },
];

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diffMs = Date.now() - d;
  const diffHrs = diffMs / 3600000;
  if (diffHrs < 1) return `${Math.max(1, Math.round(diffMs / 60000))}m ago`;
  if (diffHrs < 24) return `${Math.round(diffHrs)}h ago`;
  if (diffHrs < 48) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function ScoreBadge({ score, size = 'sm' }) {
  const color = score >= 80 ? 'text-emerald-400 bg-emerald-500/15 border-emerald-500/25'
    : score >= 60 ? 'text-amber-400 bg-amber-500/15 border-amber-500/25'
    : 'text-red-400 bg-red-500/15 border-red-500/25';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : 'Needs Work';
  const sizeClass = size === 'lg'
    ? 'h-12 w-12 text-lg font-bold'
    : 'h-7 w-7 text-[10px] font-semibold';
  return (
    <div className="flex items-center gap-1.5" data-testid="score-badge">
      <div className={`${sizeClass} rounded-full border ${color} flex items-center justify-center`}>
        {score}
      </div>
      {size === 'lg' && <span className={`text-xs ${color.split(' ')[0].replace('bg-', 'text-').replace('/15', '')}`}>{label}</span>}
    </div>
  );
}

function SkillBar({ label, score }) {
  const color = score >= 70 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="space-y-1" data-testid={`skill-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex justify-between text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-foreground font-medium">{score}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}


// ── Call Detail Panel ──────────────────────────────────────────────────

function CallDetailPanel({ call, onClose, token }) {
  const navigate = useNavigate();
  const [scorecard, setScorecard] = useState(null);
  const [loadingSc, setLoadingSc] = useState(false);
  const [showSkills, setShowSkills] = useState(false);

  const isOutbound = call._type === 'outbound' || call.direction === 'outbound';
  const intentInfo = INTENT_LABELS[call.intent] || INTENT_LABELS.general_question;
  const outcomeInfo = OUTCOME_BADGES_MAP[call.outcome] || OUTCOME_BADGES_MAP.completed;
  const history = call.conversation_history || [];
  const transcriptLines = Array.isArray(call.transcript) ? call.transcript : [];

  useEffect(() => {
    if (!call.id || isOutbound) return;
    setLoadingSc(true);
    axios.get(`${API}/calls/logs/${call.id}/scorecard`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setScorecard(r.data)).catch(() => {}).finally(() => setLoadingSc(false));
  }, [call.id, token, isOutbound]);

  const highlights = scorecard?.transcript_highlights || [];
  const highlightMap = {};
  highlights.forEach(h => { highlightMap[h.index] = h.tag; });

  const tagColor = { CTA: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', OBJECTION: 'bg-red-500/20 text-red-400 border-red-500/30', HIGH_INTENT: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-end" onClick={onClose} data-testid="call-detail-overlay">
      <div className="w-full sm:max-w-lg bg-card border-l border-border h-full overflow-y-auto animate-in slide-in-from-right" onClick={e => e.stopPropagation()} data-testid="call-detail-panel">

        {/* Header */}
        <div className="sticky top-0 bg-card/95 backdrop-blur border-b border-border p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              {isOutbound ? <PhoneOutgoing size={16} className="text-emerald-400" /> : <PhoneIncoming size={16} className="text-blue-400" />}
              {isOutbound ? 'Outbound AI Call' : 'Call Details'}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{formatFullDate(call.started_at || call.created_at)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" data-testid="close-detail-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Call info */}
          <div className="grid grid-cols-2 gap-3">
            <InfoBlock label={isOutbound ? "Called" : "Caller"} value={call.phone || call.caller_number || 'Unknown'} />
            <InfoBlock label="Duration" value={formatDuration(call.duration_sec || call.duration_seconds)} />
            <InfoBlock label="Outcome" value={<Badge className={`text-xs ${outcomeInfo.color}`} data-testid="outcome-badge">{outcomeInfo.label}</Badge>} />
            <InfoBlock label="Intent" value={<Badge className={`text-xs ${intentInfo.color}`} data-testid="intent-badge">{intentInfo.label}</Badge>} />
            {call.recording_url && <InfoBlock label="Recording" value={<Badge className="text-xs bg-violet-500/10 text-violet-400 border-violet-500/20"><Mic size={10} className="mr-1 inline" /> Available</Badge>} />}
          </div>

          {/* Recording */}
          {call.recording_url && <RecordingPlayer url={call.recording_url} />}

          {/* Out-of-Market */}
          {call.out_of_market && (
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 flex items-start gap-2" data-testid="out-of-market-banner">
              <PhoneForwarded size={16} className="text-orange-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-400">Out-of-Market Lead</p>
                {call.requested_area && <p className="text-xs text-orange-300/70 mt-0.5">Requested area: <span className="font-medium text-orange-300">{call.requested_area}</span></p>}
              </div>
            </div>
          )}

          {/* ── CONVERSION SCORECARD ── */}
          {scorecard && (
            <div className="space-y-4" data-testid="conversion-scorecard">
              <div className="flex items-center gap-2">
                <Target size={14} className="text-violet-400" />
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Conversion Scorecard</h3>
              </div>

              {/* Score + Outcome row */}
              <div className="bg-muted rounded-xl border border-border/40 p-4">
                <div className="flex items-center justify-between mb-4">
                  <ScoreBadge score={scorecard.conversion_score} size="lg" />
                  <div className="flex gap-2">
                    <Badge className={`text-[10px] ${SCORECARD_OUTCOME_COLORS[scorecard.outcome] || ''}`} data-testid="scorecard-outcome">{scorecard.outcome}</Badge>
                    <Badge className={`text-[10px] ${INTENT_TYPE_COLORS[scorecard.intent_type] || ''}`} data-testid="scorecard-intent">{scorecard.intent_type}</Badge>
                  </div>
                </div>

                {/* Quick flags */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <FlagPill label="Booking" value={scorecard.booking_result === 'success' ? 'Yes' : scorecard.booking_result === 'fail' ? 'Attempted' : 'No'} positive={scorecard.booking_result === 'success'} />
                  <FlagPill label="Missed Opp" value={scorecard.missed_opportunity ? 'Yes' : 'No'} positive={!scorecard.missed_opportunity} />
                  <FlagPill label="Objection" value={scorecard.objection_detected || 'None'} positive={!scorecard.objection_detected} />
                </div>

                {/* Skills */}
                <button onClick={() => setShowSkills(!showSkills)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2" data-testid="toggle-skills-btn">
                  {showSkills ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  Skill Breakdown
                </button>
                {showSkills && (
                  <div className="space-y-2.5 animate-in fade-in" data-testid="skills-breakdown">
                    <SkillBar label="Qualification Quality" score={scorecard.skills.qualification_quality} />
                    <SkillBar label="CTA Timing" score={scorecard.skills.cta_timing} />
                    <SkillBar label="Objection Handling" score={scorecard.skills.objection_handling} />
                    <SkillBar label="Conversation Flow" score={scorecard.skills.conversation_flow} />
                    <SkillBar label="Escalation Accuracy" score={scorecard.skills.escalation_accuracy} />
                  </div>
                )}
              </div>

              {/* AI Insights */}
              {scorecard.insights && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ai-insights">
                  <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1"><CheckCircle2 size={10} /> What Went Well</p>
                    <ul className="space-y-1.5">
                      {scorecard.insights.went_well.map((w, i) => (
                        <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                          <span className="text-emerald-500 mt-0.5 shrink-0">+</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1"><AlertTriangle size={10} /> To Improve</p>
                    <ul className="space-y-1.5">
                      {scorecard.insights.to_improve.map((w, i) => (
                        <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                          <span className="text-amber-500 mt-0.5 shrink-0">-</span> {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Suggested Actions */}
              {scorecard.suggested_actions?.length > 0 && (
                <div data-testid="suggested-actions">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1"><Zap size={10} /> Suggested Actions</p>
                  <div className="flex flex-wrap gap-2">
                    {scorecard.suggested_actions.map((a, i) => (
                      <Button key={i} size="sm" variant="outline"
                        className="h-7 text-[10px] border-border text-foreground hover:bg-muted hover:text-foreground gap-1"
                        data-testid={`action-${a.type}`}
                        onClick={() => {
                          if (a.type === 'simulation') navigate(`/call-simulation?scenario=${a.scenario}`);
                          else if (a.type === 'followup' && a.lead_id) navigate(`/pipeline?lead=${a.lead_id}`);
                          else toast.info(a.text);
                        }}>
                        {a.type === 'simulation' ? <Play size={10} /> : a.type === 'followup' ? <ArrowUpRight size={10} /> : <MessageSquare size={10} />}
                        {a.text}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {loadingSc && !isOutbound && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
              <div className="h-3 w-3 rounded-full border border-slate-500 border-t-transparent animate-spin" />
              Loading scorecard...
            </div>
          )}

          {/* Summary */}
          {call.summary && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">AI Summary</h3>
              <p className="text-sm text-foreground bg-muted rounded-lg p-3" data-testid="call-summary">{call.summary}</p>
            </div>
          )}

          {/* Transcript with highlights */}
          {transcriptLines.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">AI Conversation ({transcriptLines.length} lines)</h3>
              <div className="space-y-2" data-testid="outbound-transcript">
                {transcriptLines.map((line, i) => {
                  const isAssistant = typeof line === 'string' ? line.startsWith('Assistant:') : line.role === 'assistant';
                  const text = typeof line === 'string' ? line.replace(/^(Assistant|Caller): /, '') : line.content;
                  const tag = highlightMap[history.length + i];
                  return (
                    <div key={i} className={`rounded-lg p-3 text-sm relative ${isAssistant ? 'bg-emerald-500/5 border border-emerald-500/10 mr-4' : 'bg-blue-500/5 border border-blue-500/10 ml-4'} ${tag ? 'ring-1 ring-offset-1 ring-offset-slate-900 ' + (tagColor[tag]?.split(' ')[0] || 'ring-slate-600') : ''}`} data-testid={`transcript-line-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className={`text-xs font-medium ${isAssistant ? 'text-emerald-400' : 'text-blue-400'}`}>{isAssistant ? 'AI Assistant' : 'Person'}</p>
                        {tag && <Badge className={`text-[9px] ${tagColor[tag] || ''}`}>{tag}</Badge>}
                      </div>
                      <p className="text-foreground">{text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Inbound conversation history with highlights */}
          {history.length > 0 && transcriptLines.length === 0 && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Conversation ({history.length} turns)</h3>
              <div className="space-y-2" data-testid="conversation-history">
                {history.map((turn, i) => {
                  const tag = highlightMap[i];
                  return (
                    <div key={i} className={`rounded-lg p-3 text-sm relative ${turn.role === 'user' ? 'bg-blue-500/5 border border-blue-500/10 ml-4' : 'bg-emerald-500/5 border border-emerald-500/10 mr-4'} ${tag ? 'ring-1 ring-offset-1 ring-offset-slate-900' : ''}`} data-testid={`turn-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className={`text-xs font-medium ${turn.role === 'user' ? 'text-blue-400' : 'text-emerald-400'}`}>{turn.role === 'user' ? 'Caller' : 'Assistant'}</p>
                        {tag && <Badge className={`text-[9px] ${tagColor[tag] || ''}`}>{tag}</Badge>}
                      </div>
                      <p className="text-foreground">{turn.content}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Plain transcript fallback */}
          {!history.length && transcriptLines.length === 0 && call.transcript && typeof call.transcript === 'string' && (
            <div>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Transcript</h3>
              <pre className="text-sm text-foreground bg-muted rounded-lg p-3 whitespace-pre-wrap font-sans">{call.transcript}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Sub-components ─────────────────────────────────────────────────────

function FlagPill({ label, value, positive }) {
  return (
    <div className={`rounded-lg p-2 text-center border ${positive ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-muted border-border/30'}`}>
      <p className="text-[9px] text-muted-foreground uppercase">{label}</p>
      <p className={`text-[11px] font-medium ${positive ? 'text-emerald-400' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div className="bg-muted rounded-lg p-3">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  );
}

function RecordingPlayer({ url }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useState(null);

  const handleRef = (el) => {
    if (el && !audioRef[0]) {
      audioRef[0] = el;
      el.addEventListener('loadedmetadata', () => setDuration(el.duration));
      el.addEventListener('timeupdate', () => { setCurrentTime(el.currentTime); setProgress(el.duration ? (el.currentTime / el.duration) * 100 : 0); });
      el.addEventListener('ended', () => { setIsPlaying(false); setProgress(0); setCurrentTime(0); });
    }
  };

  const toggle = () => { const a = audioRef[0]; if (!a) return; if (isPlaying) a.pause(); else a.play().catch(() => {}); setIsPlaying(!isPlaying); };
  const seek = (e) => { const a = audioRef[0]; if (!a?.duration) return; const r = e.currentTarget.getBoundingClientRect(); a.currentTime = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * a.duration; };
  const fmt = (s) => { if (!s || isNaN(s)) return '0:00'; return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`; };

  return (
    <div data-testid="recording-player">
      <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5"><Volume2 size={12} /> Call Recording</h3>
      <div className="bg-muted border border-border rounded-xl p-3.5">
        <audio ref={handleRef} src={url} preload="metadata"><track kind="captions" /></audio>
        <div className="flex items-center gap-3">
          <button onClick={toggle} className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-all ${isPlaying ? 'bg-violet-500 text-foreground shadow-lg shadow-violet-500/25' : 'bg-violet-500/15 text-violet-400 hover:bg-violet-500/25'}`} data-testid="play-recording-btn">
            {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="h-1.5 bg-accent rounded-full cursor-pointer group" onClick={seek} data-testid="recording-progress">
              <div className="h-full bg-violet-500 rounded-full transition-all relative" style={{ width: `${progress}%` }}>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-violet-400 border-2 border-border opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-muted-foreground tabular-nums">{fmt(currentTime)}</span>
              <span className="text-[10px] text-muted-foreground tabular-nums">{fmt(duration)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Call Row ───────────────────────────────────────────────────────────

function CallRow({ call, onClick }) {
  const intentInfo = INTENT_LABELS[call.intent] || INTENT_LABELS.general_question;
  const isOutbound = call._type === 'outbound' || call.direction === 'outbound';
  const sc = call.scorecard;
  const DirectionIcon = isOutbound ? PhoneOutgoing : call.transfer_status === 'transferred' ? PhoneForwarded : PhoneIncoming;

  return (
    <div onClick={onClick} className="flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-muted cursor-pointer transition-colors border-b border-border last:border-0" data-testid={`call-row-${call.id}`}>
      {/* Direction icon */}
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${isOutbound ? 'bg-emerald-500/10' : 'bg-blue-500/10'}`}>
        <DirectionIcon size={16} className={isOutbound ? 'text-emerald-400' : 'text-blue-400'} />
      </div>

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground truncate">{call.phone || call.caller_number || 'Unknown'}</span>
          <Badge className={`text-[10px] ${intentInfo.color}`}>{intentInfo.label}</Badge>
          {call.out_of_market && <Badge className="text-[9px] bg-orange-500/10 text-orange-400 border-orange-500/20">OOM</Badge>}
          {call.recording_url && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20"><Mic size={9} className="text-violet-400" /></span>}
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{call.summary?.slice(0, 80) || 'Call log'}</p>
      </div>

      {/* Scorecard columns */}
      {sc && !isOutbound && (
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <ScoreBadge score={sc.conversion_score} />
          <Badge className={`text-[9px] min-w-[60px] justify-center ${SCORECARD_OUTCOME_COLORS[sc.outcome] || ''}`} data-testid="row-outcome">{sc.outcome}</Badge>
          <Badge className={`text-[9px] min-w-[40px] justify-center ${INTENT_TYPE_COLORS[sc.intent_type] || ''}`} data-testid="row-intent-type">{sc.intent_type}</Badge>
          {sc.objection_detected && <Badge className="text-[9px] bg-red-500/10 text-red-400 border-red-500/20" data-testid="row-objection">{sc.objection_detected}</Badge>}
        </div>
      )}

      {/* Time + status */}
      <div className="text-right shrink-0">
        <p className="text-xs text-muted-foreground">{formatTime(call.started_at || call.created_at)}</p>
        <div className="flex items-center gap-2 mt-0.5 justify-end">
          {(call.duration_seconds > 0 || call.duration_sec > 0) && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock size={10} /> {formatDuration(call.duration_seconds || call.duration_sec)}</span>
          )}
          <Badge className={`text-[10px] ${STATUS_STYLES[call.status] || STATUS_STYLES.initiated}`}>{call.status}</Badge>
        </div>
      </div>
      <ArrowRight size={14} className="text-muted-foreground shrink-0" />
    </div>
  );
}


// ── Main Page ──────────────────────────────────────────────────────────

export default function CallHistoryPage() {
  const { token } = useAuth();
  const [calls, setCalls] = useState([]);
  const [outboundCalls, setOutboundCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);
  const [tab, setTab] = useState('all');
  const [perfFilter, setPerfFilter] = useState('');

  const hdrs = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [callsRes, statsRes, outboundRes] = await Promise.all([
        axios.get(`${API}/calls/logs?limit=100`, { headers: hdrs }),
        axios.get(`${API}/calls/stats`, { headers: hdrs }),
        axios.get(`${API}/calls/outbound?limit=100`, { headers: hdrs }).catch(() => ({ data: [] })),
      ]);
      setCalls(callsRes.data);
      setStats(statsRes.data);
      setOutboundCalls(Array.isArray(outboundRes.data) ? outboundRes.data : []);
    } catch (err) {
      console.error('Failed to fetch call history:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCallDetail = async (call) => {
    try {
      if (call._type === 'outbound') {
        const { data } = await axios.get(`${API}/calls/outbound/${call.id}/transcript`, { headers: hdrs });
        setSelectedCall({ ...call, ...data, _type: 'outbound' });
      } else {
        const { data } = await axios.get(`${API}/calls/logs/${call.id}`, { headers: hdrs });
        setSelectedCall(data);
      }
    } catch {
      setSelectedCall(call);
    }
  };

  // Merge
  const allCalls = [
    ...calls.map(c => ({ ...c, _type: 'inbound' })),
    ...outboundCalls.map(c => ({ ...c, _type: 'outbound', caller_number: c.phone, direction: 'outbound', intent: c.outcome || 'general_question', duration_seconds: c.duration_sec })),
  ].sort((a, b) => new Date(b.started_at || b.created_at) - new Date(a.started_at || a.created_at));

  // Tab filter
  let displayCalls = tab === 'inbound' ? allCalls.filter(c => c._type === 'inbound')
    : tab === 'outbound' ? allCalls.filter(c => c._type === 'outbound') : allCalls;

  // Performance filter
  if (perfFilter) {
    displayCalls = displayCalls.filter(c => {
      const sc = c.scorecard;
      if (!sc) return perfFilter === '';
      switch (perfFilter) {
        case 'high_score': return sc.conversion_score >= 80;
        case 'missed': return sc.outcome === 'Missed';
        case 'objection': return !!sc.objection_detected;
        case 'buyer': return sc.intent_type === 'Buyer';
        case 'seller': return sc.intent_type === 'Seller';
        case 'listing': return sc.intent_type === 'Listing';
        default: return true;
      }
    });
  }

  // Compute score stats for header
  const scoredCalls = calls.filter(c => c.scorecard?.conversion_score);
  const avgScore = scoredCalls.length ? Math.round(scoredCalls.reduce((a, c) => a + c.scorecard.conversion_score, 0) / scoredCalls.length) : 0;
  const highScoreCount = scoredCalls.filter(c => c.scorecard.conversion_score >= 80).length;
  const missedCount = calls.filter(c => c.scorecard?.outcome === 'Missed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="call-history-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-foreground">Call History</h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">View and analyze your AI assistant's call performance</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Total Calls" value={(stats?.total_calls || 0) + outboundCalls.length} icon={<BarChart3 size={14} />} />
        <StatCard label="Avg Score" value={avgScore || '—'} accent="violet" icon={<Target size={14} />} />
        <StatCard label="High Score" value={highScoreCount} accent="emerald" icon={<TrendingUp size={14} />} />
        <StatCard label="Missed Opps" value={missedCount} accent="amber" icon={<AlertTriangle size={14} />} />
        <StatCard label="Inbound / Out" value={`${stats?.total_calls || 0} / ${outboundCalls.length}`} accent="blue" icon={<PhoneIncoming size={14} />} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border pb-1">
        {[
          { key: 'all', label: 'All Calls' },
          { key: 'inbound', label: 'Inbound' },
          { key: 'outbound', label: 'Outbound AI' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${tab === t.key ? 'bg-muted text-foreground border-b-2 border-emerald-500' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
            data-testid={`tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {/* Performance Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="perf-filters">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0"><Filter size={12} /></div>
        {PERF_FILTERS.map(f => (
          <button key={f.key} onClick={() => setPerfFilter(perfFilter === f.key ? '' : f.key)}
            className={`px-2.5 py-1 rounded-full text-xs transition-colors shrink-0 ${perfFilter === f.key ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-muted text-muted-foreground border border-border hover:bg-muted hover:text-foreground'}`}
            data-testid={`filter-${f.key || 'all'}`}>{f.label}</button>
        ))}
      </div>

      {/* Table header */}
      <div className="hidden sm:flex items-center gap-3 px-4 py-2 text-[10px] text-muted-foreground uppercase tracking-wider border-b border-border">
        <div className="w-9" />
        <div className="flex-1">Call</div>
        <div className="w-8 text-center">Score</div>
        <div className="w-[60px] text-center">Outcome</div>
        <div className="w-[40px] text-center">Intent</div>
        <div className="w-[60px] text-center">Objection</div>
        <div className="w-28 text-right">Time</div>
        <div className="w-3.5" />
      </div>

      {/* Call list */}
      <Card className="border-border bg-card overflow-hidden" data-testid="call-list-card">
        <CardContent className="p-0">
          {displayCalls.length === 0 ? (
            <div className="py-12 text-center">
              <PhoneIncoming className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{perfFilter ? 'No calls match this filter' : 'No calls yet'}</p>
              <p className="text-xs text-muted-foreground mt-1">Calls will appear here when your AI assistant handles them</p>
            </div>
          ) : (
            displayCalls.map(call => (
              <CallRow key={call.id} call={call} onClick={() => openCallDetail(call)} />
            ))
          )}
        </CardContent>
      </Card>

      {/* Detail panel */}
      {selectedCall && <CallDetailPanel call={selectedCall} onClose={() => setSelectedCall(null)} token={token} />}
    </div>
  );
}

function StatCard({ label, value, accent = 'slate', icon }) {
  const colors = { slate: 'text-foreground', emerald: 'text-emerald-400', amber: 'text-amber-400', blue: 'text-blue-400', violet: 'text-violet-400' };
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-lg sm:text-xl font-bold ${colors[accent]}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">{label}</p>
          </div>
          <div className="text-muted-foreground">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}
